import { DialogContent, DialogTitle, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { paperGoodsReceiptBusiness } from "api/bussiness/paper-material/paper-goods-receipt";
import { invalidatePaperCatalogCache } from "api/cached";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import { createPaperMaterialOptionLabel } from "commons/ultilities/option-item/label-ultil";
import { observer } from "mobx-react-lite";
import { IUpdatePaperGoodsReceipt } from "mobx-stores/_models/paper-inventory/paper-goods-receipt/paper-goods-receipt";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getUpdatePaperGoodsReceiptSchema } from "./schema";

export const UpdatePaperGoodsReceipt = observer(() => {
  const queryClient = useQueryClient();
  const {
    paperInventoryManagement: {
      paperGoodsReceiptStore: {
        openEdit,
        paperFilterValue,
        getPaperGoodsReceipt,
        paperGoodsReceiptDetail,
        pagination,
        setProp,
      },
    },
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    submitHandler,
    setDefaults,
  } = useForm<IUpdatePaperGoodsReceipt>(getUpdatePaperGoodsReceiptSchema(), {
    id: paperGoodsReceiptDetail?.id,
    receiptDate: paperGoodsReceiptDetail?.receiptDate,
    items: [],
  });

  useEffect(() => {
    if (paperGoodsReceiptDetail) {
      setDefaults({
        id: paperGoodsReceiptDetail?.id,
        receiptDate: paperGoodsReceiptDetail?.receiptDate,
        items: paperGoodsReceiptDetail.items.map((x) => ({
          id: x.id,
          materialId: x.materialId,
          amount: x.amount,
          wareHouse: x.wareHouse,
          brand: x.brand,
          serialNumber: x.serialNumber,
          supplier: x.supplier,
          document: x.document,
          note: x.note,
          origin: x.origin,
          rollNumber: x.rollNumber,
        })),
      });

      if (
        paperGoodsReceiptDetail.items &&
        paperGoodsReceiptDetail.items.length > 0
      ) {
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = paperGoodsReceiptDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value,
          )?.[0];

          const materialIdCtr = row.filter((x) => x.name === "materialId")?.[0];
          if (materialIdCtr && data?.material) {
            materialIdCtr.setProp("optionItems", [
              {
                label: createPaperMaterialOptionLabel(data.material),
                value: data.material?.id,
              },
            ]);
          }
        });
      }
    }
  }, [
    controls,
    getControl,
    paperGoodsReceiptDetail,
    paperGoodsReceiptDetail?.id,
    paperGoodsReceiptDetail?.receiptDate,
    paperGoodsReceiptDetail?.items,
    setDefaults,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdatePaperGoodsReceipt) => {
      try {
        if (!paperGoodsReceiptDetail?.id) return;
        await paperGoodsReceiptBusiness.update(
          paperGoodsReceiptDetail?.id,
          data,
        );
        await invalidatePaperCatalogCache(queryClient);
        await getPaperGoodsReceipt({
          filter: pagination.filter,
          maxResultCount: pagination.itemsPerPage,
          skipCount: pagination.skipCount,
          sorting: "",
          materialId: paperFilterValue,
        });
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [
      getPaperGoodsReceipt,
      pagination.filter,
      pagination.itemsPerPage,
      pagination.skipCount,
      paperFilterValue,
      paperGoodsReceiptDetail?.id,
      queryClient,
      setProp,
    ],
  );

  const itemsControl = useMemo(() => {
    return getControl("items").children;
  }, [getControl]);

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"sm"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa nhập kho</DialogTitle>
      <DialogContent>
        <Stack sx={{ marginTop: "10px" }}>
          <DynamicForm controls={controls}></DynamicForm>
          {itemsControl && itemsControl.length > 0 && (
            <DynamicFormArray
              children={itemsControl}
              controlSpacing={2}
            ></DynamicFormArray>
          )}
        </Stack>
      </DialogContent>
    </DialogForm>
  );
});
